import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Button,
  Input,
  Spinner,
  useTableFeatures,
  useTableSort,
  createTableColumn,
} from "@fluentui/react-components";
import { CiFilter } from "react-icons/ci";

import { Delete20Regular, Add20Filled, Edit20Regular, Flowchart20Regular, Eye20Regular } from "@fluentui/react-icons";
import "../CSS/ListCallFlowsComponent.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import { DeleteCallFlow, GetAllCallFlow } from "../../Services/HeedifyServices";
import { getAccessToken, getAuthenticatedClient, getUserPrincipalNames } from "../../Services/GraphServices";
import { useMsal } from "@azure/msal-react";
import { useAbility } from "../../Contexts/AbilityContext";
import { useNotification } from '../GenericComponent/NotificationSystem';

const ListCallFlowsComponent = ({ st_CallFlows, setCallFlows, ACTIVE_TENANT, SCOPES_NAMES }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;

  const ability = useAbility();
  const { showSuccess, showError } = useNotification();

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedCallFlow, setSelectedCallFlow] = useState(null);
  const [loadingDeleteId, setLoadingDeleteId] = useState(null);
  const [filterVisible, setFilterVisible] = useState({ NAME: false, RESOURCE_ACCOUNT: false, SCOPE: false, TIMEZONE: false, LANGUAGE: false });
  const [filters, setFilters] = useState({ NAME: "", RESOURCE_ACCOUNT: "", SCOPE: "", TIMEZONE: "", LANGUAGE: "" });
  // Adresse du resource account (compte@domaine). Les flows créés récemment la portent déjà
  // (`userPrincipalName`, posé à la création) ; les autres ne l'ont pas en base — l'id d'un call
  // flow ÉTANT celui de son resource account, on la lit alors dans l'annuaire (Graph). Gardée à
  // part pour que le tableau s'affiche sans attendre la réponse.
  const [upnById, setUpnById] = useState({});
  const upnOf = useCallback(
    (flow) => flow?.userPrincipalName || upnById[flow?.id] || "",
    [upnById]
  );

  // ⚡ Initialisation des rows avec permissions
  useEffect(() => {
    if (!st_CallFlows || !ability) return;

    // 🔐 Convertir en tableau si c'est un objet
    const callFlowsArray = Array.isArray(st_CallFlows) 
      ? st_CallFlows 
      : Object.values(st_CallFlows || {});

    if (callFlowsArray.length === 0) {
      setRows([]);
      setFilteredRows([]);
      return;
    }

    // Dédupliquer les CallFlows par ID au cas où
    const uniqueFlows = Array.from(
      new Map(callFlowsArray.map(flow => [flow.id, flow])).values()
    );

    // 🔐 Calculer permissions pour chaque callflow selon son scope
    const updatedRows = uniqueFlows.map(flow => {
      const flowScope = flow.scope || "default";
      return {
        ...flow,
        permissions: {
          main: {
            canCreate: ability?.can("create", `Flow.main@${flowScope}`),
            canRead: ability?.can("read", `Flow.main@${flowScope}`),
            canUpdate: ability?.can("update", `Flow.main@${flowScope}`),
            canDelete: ability?.can("delete", `Flow.main@${flowScope}`),
          },
        },
      };
    });

    setRows(updatedRows);
    setFilteredRows(updatedRows.filter(f => f.permissions?.main?.canRead));
  }, [st_CallFlows, ability]);

  // --- Adresses manquantes, lues dans l'annuaire. On ne demande QUE les flows dont la base ne
  // porte pas déjà l'UPN : sur un tenant à jour, aucun appel Graph n'est nécessaire.
  useEffect(() => {
    const missing = rows
      .filter(f => f.id && !f.userPrincipalName && upnById[f.id] === undefined)
      .map(f => f.id);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessToken(instance, activeAccount);
        if (!token) return;
        const names = await getUserPrincipalNames(getAuthenticatedClient(token), missing);
        if (cancelled) return;
        // Les ids sans réponse sont mémorisés à "" : sans ça, on les redemanderait à chaque
        // rendu (un compte supprimé ou non lisible ferait boucler l'écran sur Graph).
        setUpnById(prev => {
          const next = { ...prev };
          missing.forEach(id => { next[id] = names[id] || ""; });
          return next;
        });
      } catch (error) {
        console.error("Erreur lors de la lecture des resource accounts :", error);
      }
    })();

    return () => { cancelled = true; };
  }, [rows, upnById, instance, activeAccount]);

  // --- Filtrage
  useEffect(() => {
    const filtered = rows.filter(flow => {
      const hasReadPermission = flow.permissions?.main?.canRead;
      return (
        hasReadPermission &&
        flow.displayName?.toLowerCase().includes(filters.NAME.toLowerCase()) &&
        upnOf(flow).toLowerCase().includes(filters.RESOURCE_ACCOUNT.toLowerCase()) &&
        flow.scope?.toLowerCase().includes(filters.SCOPE.toLowerCase()) &&
        flow.timeZone?.toLowerCase().includes(filters.TIMEZONE.toLowerCase()) &&
        flow.language?.toLowerCase().includes(filters.LANGUAGE.toLowerCase())
      );
    });
    setFilteredRows(filtered);
  }, [filters, rows, upnOf]);
  const columns = [
    createTableColumn({ columnId: "NAME" }),
    createTableColumn({ columnId: "RESOURCE_ACCOUNT" }),
    createTableColumn({ columnId: "SCOPE" }),
    createTableColumn({ columnId: "TIMEZONE" }),
    createTableColumn({ columnId: "LANGUAGE" }),
    createTableColumn({ columnId: "ACTION" }),
  ];

  const { getRows, sort: { getSortDirection, toggleColumnSort, sort } } = useTableFeatures(
    { columns, items: filteredRows },
    [useTableSort({ defaultSortState: { sortColumn: "NAME", sortDirection: "ascending" } })]
  );

  const sortedRows = useMemo(() => sort(getRows()), [sort, getRows]);

  const headerSortProps = columnId => ({ onClick: e => toggleColumnSort(e, columnId), sortDirection: getSortDirection(columnId) });
  const handleFilterChange = (col, value) => setFilters(prev => ({ ...prev, [col]: value }));
  const toggleFilterVisibility = col => setFilterVisible(prev => ({ ...prev, [col]: !prev[col] }));

  const confirmRemove = flow => setSelectedCallFlow(flow);

  const removeCallFlow = async (flow) => {
    if (!flow.permissions?.main?.canDelete) return;
    try {
      setLoadingDeleteId(flow.id);
      const token = await getAccessToken(instance, activeAccount);
      await DeleteCallFlow(ACTIVE_TENANT, userId, flow.id, flow.scope, token);
      
      // Recharger tous les CallFlows depuis le backend pour garantir la cohérence
      const scopeNames = Array.isArray(SCOPES_NAMES) ? SCOPES_NAMES : (SCOPES_NAMES ? Object.keys(SCOPES_NAMES) : ["default"]);
      const callFlowPromises = scopeNames.map((scope) =>
        GetAllCallFlow(ACTIVE_TENANT, userId, scope, token)
      );
      const data = await Promise.all(callFlowPromises);
      
      // Gérer le cas où GetAllCallFlow retourne soit un objet, soit un tableau
      let allFlows = [];
      data.forEach(scopeData => {
        if (Array.isArray(scopeData)) {
          allFlows = [...allFlows, ...scopeData];
        } else if (scopeData && typeof scopeData === 'object') {
          allFlows = [...allFlows, ...Object.values(scopeData)];
        }
      });
      
      // Dédupliquer par ID
      const uniqueFlows = Array.from(
        new Map(allFlows.map(flow => [flow.id, flow])).values()
      );
      
      setCallFlows(uniqueFlows);
      setSelectedCallFlow(null);
      showSuccess(t("CALLFLOW_DELETED_SUCCESS"));
    } catch (error) {
      console.error("Error deleting call flow:", error);
      showError(t("CALLFLOW_DELETE_ERROR"), error?.message);
    } finally {
      setLoadingDeleteId(null);
    }
  };
  const addCallFlow = () => {
    navigate("/callflows/newCallFlow");
  };

  const selectCallFlow = flow => {
    navigate(`/callflows/${flow.id}`);
  };

  // 🔐 Vérifier si on peut créer un callflow dans au moins un scope
  const hasAnyCreatePermission = SCOPES_NAMES?.some(scope => 
    ability?.can("create", `Flow.main@${scope}`)
  );
 
  return (
    <>
      <div >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h2>{t("CALL_FLOWS_LIST")}</h2>
        <Button appearance="primary" onClick={addCallFlow} disabled={!hasAnyCreatePermission}>
          <Add20Filled /> {t("ADD_NEW_CALLFLOW")}
        </Button>
      </div>
      <div className="callflow-list-table-wrapper">
        <Table sortable>
          <TableHeader>
            <TableRow>
              {["NAME", "RESOURCE_ACCOUNT", "SCOPE", "TIMEZONE", "LANGUAGE"].map(col => (
                <TableHeaderCell key={col} {...headerSortProps(col)}>
                  {t(col)}
                  <CiFilter className="filter-icon" onClick={e => { e.stopPropagation(); toggleFilterVisibility(col); }} />
                  {filterVisible[col] && <Input className="callflow-list-input-filter-style" placeholder={t("FILTER")} value={filters[col]} onChange={e => handleFilterChange(col, e.target.value)} />}
                </TableHeaderCell>
              ))}
              <TableHeaderCell className="callflow-list-cell-right">{t("ACTION")}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRows.map(({ item, rowId }) => (
              <TableRow key={rowId}>
                <TableCell><TableCellLayout>{item.displayName}</TableCellLayout></TableCell>
                <TableCell className="callflow-list-cell-account">
                  {/* Un UPN d'invité (`...#EXT#@tenant.onmicrosoft.com`) n'offre aucun point de
                      coupure : sans troncature il débordait sur la colonne Entity. Valeur
                      complète au survol. */}
                  <TableCellLayout truncate>
                    <span className="callflow-list-account" title={upnOf(item)}>{upnOf(item) || "—"}</span>
                  </TableCellLayout>
                </TableCell>
                <TableCell><TableCellLayout>{item.scope}</TableCellLayout></TableCell>
                <TableCell><TableCellLayout>{item.timeZone}</TableCellLayout></TableCell>
                <TableCell><TableCellLayout>{item.language}</TableCellLayout></TableCell> 
                <TableCell className="callflow-list-cell-right">
                  <Button icon={item.permissions?.main?.canUpdate ? <Edit20Regular /> : <Eye20Regular />} appearance="subtle" disabled={!item.permissions?.main?.canRead} onClick={() => selectCallFlow(item)} title={t("EDIT")} />
                  <Button icon={<Flowchart20Regular />} appearance="subtle" disabled={!item.permissions?.main?.canRead} onClick={() => navigate(`/callFlows/diagram/${item.id}`)} title={t("DIAGRAM")} />
                  <Button icon={loadingDeleteId === item.id ? <Spinner size="tiny" /> : <Delete20Regular />} appearance="subtle" disabled={loadingDeleteId === item.id || !item.permissions?.main?.canDelete} onClick={() => confirmRemove(item)} title={t("DELETE")}/>  
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {selectedCallFlow && (
        <ConfirmAlert
          open={!!selectedCallFlow}
          name={selectedCallFlow.displayName}
          onConfirm={() => removeCallFlow(selectedCallFlow)}
          onCancel={() => setSelectedCallFlow(null)}
        />
      )}
      </div>
    </>
  );
};

export default ListCallFlowsComponent;
